import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { LOGO_URL } from "../constants";
import { api } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const errFromApi = (e) => {
  try {
    const msg = e?.message || "";
    const idx = msg.indexOf("{");
    if (idx >= 0) {
      const j = JSON.parse(msg.slice(idx));
      if (typeof j.detail === "string") return j.detail;
      if (Array.isArray(j.detail)) return j.detail.map((x) => x.msg).join(" ");
    }
  } catch { /* ignore */ }
  return e?.message || "Something went wrong";
};

export default function Landing() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [view, setView] = useState("login"); // "login" | "forgot"

  const redirect = searchParams.get("redirect") || null;

  useEffect(() => {
    if (!loading && user) {
      navigate(user.role === "admin" ? "/admin" : (redirect || "/dashboard"), { replace: true });
    }
  }, [user, loading, navigate, redirect]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0E0F12] flex items-center justify-center">
        <div
          style={{
            width: 28, height: 28, borderRadius: "50%",
            border: "2.5px solid rgba(207,255,80,0.2)",
            borderTopColor: "#CFFF50",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0E0F12] flex flex-col">
      {/* Subtle background glow */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute top-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[#7C5CFF]/10 blur-[140px]" />
        <div className="absolute bottom-[-15%] right-[-10%] w-[400px] h-[400px] rounded-full bg-[#CFFF50]/6 blur-[140px]" />
      </div>

      {/* Main content */}
      <div className="relative flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-[420px]">

          {/* Logo + wordmark */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center bg-black rounded-2xl px-5 py-3 mb-6">
              <img src={LOGO_URL} alt="Blue Mountain Group" className="h-10 w-auto" />
            </div>
            <h1 className="text-white font-black text-2xl tracking-tight leading-snug">
              Blue Mountain Academy
            </h1>
            <p className="mt-2 text-[#6b7280] text-sm leading-relaxed">
              Access your courses, certificates and training records.
            </p>
          </div>

          {/* Card */}
          <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-8">
            {view === "login" ? (
              <SignInForm
                redirect={redirect}
                setUser={setUser}
                navigate={navigate}
                onForgot={() => setView("forgot")}
              />
            ) : (
              <ForgotForm onBack={() => setView("login")} />
            )}
          </div>

          {/* Support */}
          <p className="mt-6 text-center text-xs text-[#4b5563]">
            Need help?{" "}
            <a
              href="mailto:support@bluemountain.io"
              className="text-[#6b7280] hover:text-white transition-colors underline underline-offset-2"
            >
              support@bluemountain.io
            </a>
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="relative text-center py-5 text-[10px] text-[#374151] tracking-wide">
        © 2026 Blue Mountain Group. All rights reserved.
      </div>
    </div>
  );
}

function SignInForm({ redirect, setUser, navigate, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [focusedEmail, setFocusedEmail] = useState(false);
  const [focusedPw, setFocusedPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      setUser(res.user);
      toast.success(`Welcome back, ${res.user.name}`);
      const dest = res.user.role === "admin" ? "/admin" : (redirect || "/dashboard");
      navigate(dest, { replace: true });
    } catch (err) {
      toast.error(errFromApi(err));
    } finally {
      setBusy(false);
    }
  };

  const fieldBase =
    "w-full bg-black/30 border rounded-xl px-4 py-3 text-sm text-white placeholder-[#4b5563] outline-none transition-colors duration-150";
  const fieldFocused = "border-[#CFFF50]/40";
  const fieldIdle = "border-white/[0.08]";

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-xs text-[#6b7280] mb-1.5 font-medium">
          Email address
        </label>
        <div className="relative">
          <Mail
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4b5563] pointer-events-none"
          />
          <input
            data-testid="login-email"
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocusedEmail(true)}
            onBlur={() => setFocusedEmail(false)}
            placeholder="you@example.com"
            className={`${fieldBase} pl-9 ${focusedEmail ? fieldFocused : fieldIdle}`}
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-xs text-[#6b7280] font-medium">Password</label>
          <button
            type="button"
            onClick={onForgot}
            data-testid="forgot-link"
            className="text-xs text-[#6b7280] hover:text-white transition-colors"
          >
            Forgot password?
          </button>
        </div>
        <div className="relative">
          <Lock
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4b5563] pointer-events-none"
          />
          <input
            data-testid="login-password"
            type={showPw ? "text" : "password"}
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onFocus={() => setFocusedPw(true)}
            onBlur={() => setFocusedPw(false)}
            placeholder="••••••••"
            className={`${fieldBase} pl-9 pr-10 ${focusedPw ? fieldFocused : fieldIdle}`}
          />
          <button
            type="button"
            onClick={() => setShowPw(!showPw)}
            className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[#4b5563] hover:text-white transition-colors"
          >
            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        data-testid="login-submit"
        disabled={busy}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#CFFF50] text-[#0E0F12] font-bold text-sm hover:bg-[#d8ff6a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Signing in…
          </>
        ) : (
          "Sign in"
        )}
      </button>
    </form>
  );
}

function ForgotForm({ onBack }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [focused, setFocused] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email });
      toast.success("If that email is registered we've sent a reset link.");
      setEmail("");
      onBack();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-xs text-[#6b7280] hover:text-white transition-colors mb-2"
      >
        <ArrowLeft size={13} /> Back to sign in
      </button>

      <div>
        <h2 className="text-white font-bold text-lg mb-1">Reset password</h2>
        <p className="text-xs text-[#6b7280] leading-relaxed">
          Enter your email and we'll send a reset link if the account exists.
        </p>
      </div>

      <div>
        <label className="block text-xs text-[#6b7280] mb-1.5 font-medium">
          Email address
        </label>
        <div className="relative">
          <Mail
            size={14}
            className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4b5563] pointer-events-none"
          />
          <input
            data-testid="forgot-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="you@example.com"
            className={`w-full bg-black/30 border rounded-xl px-4 py-3 pl-9 text-sm text-white placeholder-[#4b5563] outline-none transition-colors duration-150 ${focused ? "border-[#CFFF50]/40" : "border-white/[0.08]"}`}
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="forgot-submit"
        disabled={busy}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#CFFF50] text-[#0E0F12] font-bold text-sm hover:bg-[#d8ff6a] transition-colors disabled:opacity-50"
      >
        {busy ? (
          <>
            <Loader2 size={14} className="animate-spin" /> Sending…
          </>
        ) : (
          "Send reset link"
        )}
      </button>
    </form>
  );
}
